import { useState } from 'react';
import { adminApi } from '../../services/api';
import DeviceModal from './DeviceModal';
import type { Device } from '../../types/admin';

interface DeviceListProps {
  devices: Device[];
  userId: string;
  onUpdate: () => void;
}

const DeviceTypeLabels: Record<number, string> = {
  0: 'Smartphone',
  1: 'Tablet',
  2: 'Laptop',
  3: 'Desktop',
  4: 'Television',
  5: 'Streaming Device',
  6: 'Router',
  7: 'Modem',
  8: 'Smart Speaker',
  9: 'Printer',
  10: 'Camera',
  11: 'Smart Watch',
  12: 'Gaming Console',
  13: 'Smart Home',
  14: 'Other'
};

export default function DeviceList({ devices, userId, onUpdate }: DeviceListProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleAddDevice = () => {
    setEditingDevice(null);
    setShowModal(true);
  };

  const handleEditDevice = (device: Device) => {
    setEditingDevice(device);
    setShowModal(true);
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!confirm('Are you sure you want to delete this device?')) {
      return;
    }

    try {
      setDeleting(deviceId);
      await adminApi.deleteDevice(deviceId);
      onUpdate();
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Failed to delete device');
    } finally {
      setDeleting(null);
    }
  };

  const handleModalClose = (updated: boolean) => {
    setShowModal(false);
    setEditingDevice(null);
    if (updated) {
      onUpdate();
    }
  };

  return (
    <div className="device-list-container">
      <div className="section-header">
        <h2>User Devices</h2>
        <button onClick={handleAddDevice} className="btn-primary">
          + Add Device
        </button>
      </div>

      {devices.length === 0 ? (
        <div className="empty-state">
          <p>No devices added yet</p>
          <p className="hint">Add devices to personalize AI responses</p>
        </div>
      ) : (
        <div className="devices-grid">
          {devices.map(device => (
            <div key={device.id} className="device-card">
              <div className="device-header">
                <h3>{device.name}</h3>
                <span className={`status-badge ${device.isActive ? 'active' : 'inactive'}`}>
                  {device.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="device-details">
                <div className="detail-row">
                  <span className="label">Type:</span>
                  <span className="value">{DeviceTypeLabels[device.type]}</span>
                </div>
                
                {device.brand && (
                  <div className="detail-row">
                    <span className="label">Brand:</span>
                    <span className="value">{device.brand}</span>
                  </div>
                )}
                
                {device.model && (
                  <div className="detail-row">
                    <span className="label">Model:</span>
                    <span className="value">{device.model}</span>
                  </div>
                )}
                
                {device.notes && (
                  <div className="device-notes">
                    <span className="label">Notes:</span>
                    <p>{device.notes}</p>
                  </div>
                )}
              </div>

              <div className="device-actions">
                <button 
                  onClick={() => handleEditDevice(device)}
                  className="btn-secondary"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteDevice(device.id)}
                  className="btn-danger"
                  disabled={deleting === device.id}
                >
                  {deleting === device.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <DeviceModal
          userId={userId}
          device={editingDevice}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
